import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { useAsyncData } from '@/hooks';
import {
  POLICY_KEYS,
  type InstitutionPolicyOverride,
  type PlatformPolicyVersion,
  type PolicyConstraint,
  type PolicyKey,
  type PolicyMinimums,
  type PolicySource,
  type PolicyValue,
  type ResolvedPolicy,
  type ResolvedPolicySet,
} from '@/types/platform';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-11 — platform policy defaults, the CSKY security floor, and the deviations
 * individual institutions hold against them.
 *
 * THIS SCREEN USED TO DESCRIBE A PRODUCT THAT WAS NEVER BUILT. It listed
 * `/platform/policies` as rows with an id, a kind and a scope, and saved changes
 * with `PATCH /platform/policies/:id`. None of those exist. Core models policies
 * as a KEYED DOCUMENT published as a version, plus per-key overrides carrying a
 * reason and an end date — a different shape, not a renamed one — so the screen is
 * rebuilt around what is actually there.
 *
 * WHERE A VALUE CAME FROM IS THE COLUMN THAT MATTERS. A policy an institution has
 * not overridden looks identical to one it has overridden with the same value
 * until the platform default changes, at which point the two behave completely
 * differently. `source` is rendered as its own column for exactly that reason.
 *
 * A SUPPRESSED OVERRIDE IS SHOWN, NOT HIDDEN. When CSKY raises a floor above an
 * override an institution already held, that override stops applying. Rendering
 * only the winning value would show the platform default with nothing saying it
 * had ever been different, and the institution would keep believing it had the
 * setting it asked for.
 *
 * THE FLOOR IS PUBLISHED BEFORE IT IS HIT. `minimums` carries the reason for every
 * bound, and the reason is shown next to the field rather than arriving as a
 * rejection. An operator refused without an explanation learns that the platform
 * is capricious; one who reads the bound first learns where the boundary is.
 */

const SOURCE_TONES: Record<PolicySource, string> = {
  INSTITUTION_OVERRIDE: 'okTag',
  PLATFORM_VERSION: 'infoTag',
  BASELINE: 'mutedTag',
};

const MIN_REASON = 10;

type PolicyValueKind = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'LIST';

export function PoliciesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const institutionId = params.get('institutionId') ?? '';
  const [granting, setGranting] = useState<PolicyKey | null>(null);
  const [revoking, setRevoking] = useState<InstitutionPolicyOverride | null>(null);
  const [publishing, setPublishing] = useState(false);

  const minimumsFetcher = useCallback(() => platformApi.policies.minimums(), []);
  const { state: minimums, reload: reloadMinimums } = useAsyncData<PolicyMinimums>(
    'policy-minimums',
    minimumsFetcher,
  );

  const versionsFetcher = useCallback(() => platformApi.policies.versions(), []);
  const { state: versions, reload: reloadVersions } = useAsyncData<PlatformPolicyVersion[]>(
    'policy-versions',
    versionsFetcher,
  );

  const resolvedFetcher = useCallback(() => platformApi.policies.resolved(institutionId), [institutionId]);
  const { state: resolved, reload: reloadResolved } = useAsyncData<ResolvedPolicySet>(
    `policy-resolved:${institutionId}`,
    resolvedFetcher,
    institutionId.length > 0,
  );

  const overridesFetcher = useCallback(() => platformApi.policies.overrides(institutionId), [institutionId]);
  const { state: overrides, reload: reloadOverrides } = useAsyncData<InstitutionPolicyOverride[]>(
    `policy-overrides:${institutionId}`,
    overridesFetcher,
    institutionId.length > 0,
  );

  const minimumsMap: PolicyMinimums = minimums.status === 'ready' ? minimums.data : {};

  const reloadInstitution = () => {
    reloadResolved();
    reloadOverrides();
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.policies.title')}</h1>
          <p className={styles.pageLead}>{t('platform.policies.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setPublishing(true)}>
          <Icon name="plus" size={16} />
          {t('platform.policies.publish')}
        </button>
      </header>

      <Panel
        title={t('platform.policies.versionsTitle')}
        icon="history"
        actions={
          <button type="button" className={styles.secondaryBtn} onClick={reloadVersions}>
            <Icon name="refresh" size={15} />
            {t('platform.dashboard.refresh')}
          </button>
        }
      >
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.policies.versionsNotice')}
        </p>
        <AsyncPanel
          state={versions}
          onRetry={reloadVersions}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.policies.noVersions')}
          emptyBody={t('platform.policies.noVersionsBody')}
        >
          {(data) => (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.policies.versionsCaption', { n: data.length })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.policies.colVersion')}</th>
                    <th scope="col">{t('platform.policies.colStatus')}</th>
                    <th scope="col">{t('platform.policies.colEffective')}</th>
                    <th scope="col">{t('platform.policies.colChangeReason')}</th>
                    <th scope="col">{t('platform.policies.colPublishedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((version) => (
                    <tr key={version.id}>
                      <th scope="row" className={styles.tableRowHeader}>
                        {t('platform.policies.versionNumber', { n: version.versionNumber })}
                      </th>
                      <td>
                        <span
                          className={
                            version.status === 'PUBLISHED'
                              ? styles.okTag
                              : version.status === 'DRAFT'
                                ? styles.warnTag
                                : styles.mutedTag
                          }
                        >
                          {t(`platform.policies.versionStatus.${version.status}`, {
                            defaultValue: version.status,
                          })}
                        </span>
                      </td>
                      <td className={styles.mutedCell}>
                        {formatDateTime(version.effectiveFrom, locale)}
                        {version.effectiveTo ? ` → ${formatDateTime(version.effectiveTo, locale)}` : ''}
                      </td>
                      <td>{version.changeReason}</td>
                      <td className={styles.mutedCell}>{version.publishedBy ?? version.createdBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncPanel>
      </Panel>

      <Panel title={t('platform.policies.minimumsTitle')} icon="admin">
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.policies.minimumsNotice')}
        </p>
        <AsyncPanel state={minimums} onRetry={reloadMinimums}>
          {(data) => {
            const entries = POLICY_KEYS.map((key) => [key, data[key]] as const).filter(
              (entry): entry is readonly [PolicyKey, PolicyConstraint] => entry[1] !== undefined,
            );
            if (entries.length === 0) {
              return (
                <p className={styles.sectionUnavailable} role="status">
                  {t('platform.policies.noMinimums')}
                </p>
              );
            }
            return (
              <ul className={styles.ruleList}>
                {entries.map(([key, constraint]) => (
                  <li key={key} className={styles.ruleItem}>
                    <p className={styles.rowTitle}>
                      {t(`platform.policies.key.${key}`, { defaultValue: key })}{' '}
                      <span className={styles.mutedTag}>
                        {t(`platform.policies.constraint.${constraint.kind}`, {
                          defaultValue: constraint.kind,
                        })}
                      </span>
                    </p>
                    <p className={styles.rowMetaLine}>{describeBound(constraint)}</p>
                    {/* The reason, verbatim. A bound with no stated why is a rule nobody can plan around. */}
                    <p className={styles.rowDetail}>{constraint.why}</p>
                  </li>
                ))}
              </ul>
            );
          }}
        </AsyncPanel>
      </Panel>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="policies-institution">
            {t('platform.policies.institutionLabel')}
          </label>
          {/* Picking applies immediately — no UUID to paste, no submit to press. */}
          <InstitutionPicker
            id="policies-institution"
            value={institutionId}
            clearable
            onChange={(next) => setParams(next ? { institutionId: next } : {})}
          />
        </div>
        {institutionId.length > 0 && (
          <button type="button" className={styles.secondaryBtn} onClick={reloadInstitution}>
            <Icon name="refresh" size={15} />
            {t('platform.dashboard.refresh')}
          </button>
        )}
      </div>

      {institutionId.length === 0 ? (
        <Panel title={t('platform.policies.resolvedTitle')} icon="checklist">
          <p className={styles.sectionEmpty} role="status">
            {t('platform.policies.chooseInstitution')}
          </p>
        </Panel>
      ) : (
        <>
          <Panel title={t('platform.policies.resolvedTitle')} icon="checklist">
            <AsyncPanel state={resolved} onRetry={reloadResolved}>
              {(set) => (
                <>
                  <p className={styles.generatedAt}>
                    {set.platformVersionNumber === null
                      ? t('platform.policies.fromBaseline')
                      : t('platform.policies.fromVersion', { n: set.platformVersionNumber })}
                    {set.asOf ? ` · ${formatDateTime(set.asOf, locale)}` : ''}
                  </p>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <caption className={styles.tableCaption}>
                        {t('platform.policies.tableCaption', { n: set.policies.length })}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('platform.policies.colPolicy')}</th>
                          <th scope="col">{t('platform.policies.colValue')}</th>
                          <th scope="col">{t('platform.policies.colSource')}</th>
                          <th scope="col">{t('platform.policies.colFloor')}</th>
                          <th scope="col">
                            <span className={styles.srOnlyText}>{t('platform.policies.colActions')}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {set.policies.map((policy) => (
                          <PolicyRow
                            key={policy.key}
                            policy={policy}
                            constraint={minimumsMap[policy.key]}
                            onOverride={() => setGranting(policy.key)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </AsyncPanel>
          </Panel>

          <Panel title={t('platform.policies.overridesTitle')} icon="admin" emphasis="attention">
            <p className={styles.dataNotice}>
              <Icon name="info" size={15} aria-hidden="true" />
              {t('platform.policies.overridesNotice')}
            </p>
            <AsyncPanel
              state={overrides}
              onRetry={reloadOverrides}
              isEmpty={(data) => data.length === 0}
              emptyTitle={t('platform.policies.noOverrides')}
              emptyBody={t('platform.policies.noOverridesBody')}
            >
              {(data) => (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <caption className={styles.tableCaption}>
                      {t('platform.policies.overridesCaption', { n: data.length })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('platform.policies.colPolicy')}</th>
                        <th scope="col">{t('platform.policies.colValue')}</th>
                        <th scope="col">{t('platform.policies.colWindow')}</th>
                        <th scope="col">{t('platform.policies.colReason')}</th>
                        <th scope="col">{t('platform.policies.colStatus')}</th>
                        <th scope="col">
                          <span className={styles.srOnlyText}>{t('platform.policies.colActions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((override) => {
                        const revoked = Boolean(override.revokedAt);
                        return (
                          <tr key={override.id}>
                            <th scope="row" className={styles.tableRowHeader}>
                              {t(`platform.policies.key.${override.policyKey}`, {
                                defaultValue: override.policyKey,
                              })}
                            </th>
                            <td>
                              <code className={styles.code}>{describeValue(override.value)}</code>
                            </td>
                            <td className={styles.mutedCell}>
                              {formatDateTime(override.effectiveFrom, locale)}
                              {override.effectiveTo
                                ? ` → ${formatDateTime(override.effectiveTo, locale)}`
                                : ` → ${t('platform.policies.noEndDate')}`}
                            </td>
                            <td>{override.reason}</td>
                            <td>
                              <span className={revoked ? styles.mutedTag : styles.okTag}>
                                {revoked ? t('platform.policies.revoked') : t('platform.policies.inForce')}
                              </span>
                            </td>
                            <td>
                              {revoked ? (
                                <span className={styles.mutedCell}>—</span>
                              ) : (
                                <button
                                  type="button"
                                  className={styles.linkBtnDanger}
                                  onClick={() => setRevoking(override)}
                                >
                                  {t('platform.policies.revoke')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </AsyncPanel>
          </Panel>
        </>
      )}

      <GrantOverrideDialog
        institutionId={institutionId}
        policyKey={granting}
        constraint={granting ? minimumsMap[granting] : undefined}
        current={
          resolved.status === 'ready'
            ? resolved.data.policies.find((policy) => policy.key === granting)
            : undefined
        }
        onClose={() => setGranting(null)}
        onSaved={() => {
          setGranting(null);
          reloadInstitution();
        }}
      />

      <RevokeOverrideDialog
        institutionId={institutionId}
        override={revoking}
        onClose={() => setRevoking(null)}
        onSaved={() => {
          setRevoking(null);
          reloadInstitution();
        }}
      />

      <PublishVersionDialog
        open={publishing}
        currentDocument={versions.status === 'ready' ? versions.data[0]?.document : undefined}
        onClose={() => setPublishing(false)}
        onSaved={() => {
          setPublishing(false);
          reloadVersions();
          if (institutionId.length > 0) reloadInstitution();
        }}
      />
    </div>
  );
}

function PolicyRow({
  policy,
  constraint,
  onOverride,
}: {
  policy: ResolvedPolicy;
  constraint: PolicyConstraint | undefined;
  onOverride: () => void;
}) {
  const { t } = useTranslation();

  return (
    <tr>
      <th scope="row" className={styles.tableRowHeader}>
        {t(`platform.policies.key.${policy.key}`, { defaultValue: policy.key })}
        <span className={styles.tableSub}>{policy.key}</span>
      </th>
      <td>
        <code className={styles.code}>{describeValue(policy.value)}</code>
      </td>
      <td>
        <span className={styles[SOURCE_TONES[policy.source]] ?? styles.mutedTag}>
          {t(`platform.policies.source.${policy.source}`, { defaultValue: policy.source })}
        </span>
        {/*
          A suppressed override is the one thing on this screen nobody would ever
          discover on their own: the institution asked for a value, CSKY later
          raised the floor above it, and the value silently stopped applying.
        */}
        {policy.suppressedOverride && (
          <p className={styles.rowDetailError}>
            {t('platform.policies.suppressed', {
              attempted: describeValue(policy.suppressedOverride.attempted),
              minimum: policy.suppressedOverride.minimum,
            })}
          </p>
        )}
      </td>
      <td className={styles.mutedCell}>{constraint ? describeBound(constraint) : '—'}</td>
      <td>
        <button type="button" className={styles.linkBtn} onClick={onOverride}>
          {policy.source === 'INSTITUTION_OVERRIDE'
            ? t('platform.policies.replaceOverride')
            : t('platform.policies.override')}
        </button>
      </td>
    </tr>
  );
}

/** A value, rendered the same way everywhere so two cells never disagree. */
function describeValue(value: PolicyValue | undefined): string {
  if (value === undefined) return '—';
  if (Array.isArray(value)) return value.length > 0 ? `[${value.join(', ')}]` : '[]';
  return String(value);
}

function describeBound(constraint: PolicyConstraint): string {
  switch (constraint.kind) {
    case 'NUMBER_AT_LEAST':
      return `≥ ${constraint.bound ?? '—'}`;
    case 'NUMBER_AT_MOST':
      return `≤ ${constraint.bound ?? '—'}`;
    case 'BOOLEAN_MUST_REMAIN_TRUE':
      return 'true';
    case 'ENUM_AT_LEAST_AS_STRONG':
      return `≥ ${constraint.floor ?? '—'}`;
    case 'SET_SUBSET_OF':
      return `⊆ [${(constraint.permitted ?? []).join(', ')}]`;
    default:
      return '—';
  }
}

/**
 * Parses what the operator typed into one of the four shapes a policy value can be.
 *
 * Deliberately explicit rather than `JSON.parse` on everything: `12` and `"12"` are
 * different values to the floor arithmetic, and a policy stored as the string "12"
 * would compare as a string against a numeric bound. Returning `undefined` for
 * anything ambiguous makes the form refuse rather than send a shape the server
 * rejects with a message about a value nobody typed.
 */
function parsePolicyValue(text: string, kind: PolicyValueKind): PolicyValue | undefined {
  const trimmed = text.trim();
  if (trimmed.length === 0) return undefined;
  switch (kind) {
    case 'NUMBER': {
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case 'BOOLEAN':
      if (trimmed === 'true') return true;
      if (trimmed === 'false') return false;
      return undefined;
    case 'LIST': {
      const entries = trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
      return entries.length > 0 ? entries : undefined;
    }
    default:
      return trimmed;
  }
}

/** Which shape a key takes, read off the value in force rather than guessed. */
function kindOf(value: PolicyValue | undefined, constraint: PolicyConstraint | undefined): PolicyValueKind {
  if (Array.isArray(value)) return 'LIST';
  if (typeof value === 'number') return 'NUMBER';
  if (typeof value === 'boolean') return 'BOOLEAN';
  if (constraint?.kind === 'SET_SUBSET_OF') return 'LIST';
  if (constraint?.kind === 'NUMBER_AT_LEAST' || constraint?.kind === 'NUMBER_AT_MOST') return 'NUMBER';
  if (constraint?.kind === 'BOOLEAN_MUST_REMAIN_TRUE') return 'BOOLEAN';
  return 'STRING';
}

/** The editable text for a value, which is not always its display form. */
function describeRaw(value: PolicyValue): string {
  return Array.isArray(value) ? value.join(', ') : String(value);
}

function GrantOverrideDialog({
  institutionId,
  policyKey,
  constraint,
  current,
  onClose,
  onSaved,
}: {
  institutionId: string;
  policyKey: PolicyKey | null;
  constraint: PolicyConstraint | undefined;
  current: ResolvedPolicy | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentValue = current?.value;
  const kind = kindOf(currentValue, constraint);

  useEffect(() => {
    if (!policyKey) return;
    setText(currentValue === undefined ? '' : describeRaw(currentValue));
    setReason('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setError(null);
  }, [policyKey, currentValue]);

  const parsed = useMemo(() => parsePolicyValue(text, kind), [text, kind]);

  if (!policyKey) return null;

  const valid = parsed !== undefined && reason.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting || parsed === undefined) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.policies.grantOverride(institutionId, {
        policyKey,
        value: parsed,
        reason: reason.trim(),
        effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : undefined,
        effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : undefined,
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`platform.policies.key.${policyKey}`, { defaultValue: policyKey })}
      description={t('platform.policies.overrideLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        {/*
          The floor is stated before the value is typed, with the reason. An
          override may make a policy stricter and may never make it weaker than
          this; being refused is a much worse way to learn that.
        */}
        {constraint && (
          <div className={styles.warningNotice} role="note">
            <Icon name="info" size={16} aria-hidden="true" />
            <div>
              <p className={styles.warningTitle}>
                {t('platform.policies.floorTitle', { bound: describeBound(constraint) })}
              </p>
              <p className={styles.warningBody}>{constraint.why}</p>
            </div>
          </div>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.policies.colValue')}</span>
          {kind === 'BOOLEAN' ? (
            <Select
              value={text}
              onChange={setText}
              placeholder={t('platform.reports.choose')}
              options={[
                { value: 'true', label: t('platform.lifecycle.impact.yes') },
                { value: 'false', label: t('platform.lifecycle.impact.no') },
              ]}
            />
          ) : (
            <input
              className={styles.input}
              value={text}
              required
              inputMode={kind === 'NUMBER' ? 'numeric' : 'text'}
              aria-invalid={text.length > 0 && parsed === undefined}
              onChange={(event) => setText(event.target.value)}
            />
          )}
          <span className={styles.fieldHint}>{t(`platform.policies.valueHint.${kind}`)}</span>
          {text.length > 0 && parsed === undefined && (
            <span className={styles.fieldError} role="alert">
              {t('platform.policies.invalidValue')}
            </span>
          )}
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.policies.effectiveFrom')}</span>
            <input
              className={styles.input}
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
            <span className={styles.fieldHint}>{t('platform.policies.effectiveFromHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.policies.effectiveTo')}</span>
            <input
              className={styles.input}
              type="date"
              value={effectiveTo}
              min={effectiveFrom || undefined}
              onChange={(event) => setEffectiveTo(event.target.value)}
            />
            {/*
              Not required by the server, and pushed hard here anyway: most
              overrides are temporary in intent and permanent in practice, and an
              end date is what makes the review happen without anyone remembering.
            */}
            <span className={styles.fieldHint}>{t('platform.policies.effectiveToHint')}</span>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.confirm.reasonLabel')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={reason}
            required
            aria-invalid={reason.length > 0 && reason.trim().length < MIN_REASON}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.policies.grant')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RevokeOverrideDialog({
  institutionId,
  override,
  onClose,
  onSaved,
}: {
  institutionId: string;
  override: InstitutionPolicyOverride | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!override) return;
    setReason('');
    setError(null);
  }, [override]);

  if (!override) return null;

  const valid = reason.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.policies.revokeOverride(institutionId, override.id, reason.trim());
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.policies.revokeTitle', {
        policy: t(`platform.policies.key.${override.policyKey}`, { defaultValue: override.policyKey }),
      })}
      description={t('platform.policies.revokeLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.warningNotice} role="note">
          <Icon name="info" size={16} aria-hidden="true" />
          <div>
            <p className={styles.warningTitle}>{t('platform.policies.revokeEffectTitle')}</p>
            <p className={styles.warningBody}>{t('platform.policies.revokeEffectBody')}</p>
          </div>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.confirm.reasonLabel')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={reason}
            required
            aria-invalid={reason.length > 0 && reason.trim().length < MIN_REASON}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.policies.revoke')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PublishVersionDialog({
  open,
  currentDocument,
  onClose,
  onSaved,
}: {
  open: boolean;
  currentDocument: Partial<Record<PolicyKey, PolicyValue>> | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('{}');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setText(JSON.stringify(currentDocument ?? {}, null, 2));
    setEffectiveFrom('');
    setChangeReason('');
    setError(null);
  }, [open, currentDocument]);

  const parsed = useMemo(() => {
    try {
      const value: unknown = JSON.parse(text);
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
      return value as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [text]);

  /*
   * The server refuses a partial document, and the form names the missing keys
   * before it is submitted. A version that sets some policies and leaves the rest
   * answering from the in-code baseline would have two policies "from the same
   * version" that came from different decades.
   */
  const missing = parsed === null ? [] : POLICY_KEYS.filter((key) => parsed[key] === undefined);

  if (!open) return null;

  const valid =
    parsed !== null &&
    missing.length === 0 &&
    effectiveFrom.length > 0 &&
    changeReason.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting || parsed === null) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.policies.publishVersion({
        document: parsed,
        effectiveFrom: new Date(effectiveFrom).toISOString(),
        changeReason: changeReason.trim(),
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.policies.publishTitle')}
      description={t('platform.policies.publishLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.policies.documentLabel')}</span>
          <textarea
            className={styles.codeArea}
            rows={14}
            value={text}
            spellCheck={false}
            aria-invalid={parsed === null}
            aria-describedby="policy-document-hint"
            onChange={(event) => setText(event.target.value)}
          />
          <span className={styles.fieldHint} id="policy-document-hint">
            {t('platform.policies.documentHint')}
          </span>
          {parsed === null && (
            <span className={styles.fieldError} role="alert">
              {t('platform.policies.invalidJson')}
            </span>
          )}
          {parsed !== null && missing.length > 0 && (
            <span className={styles.fieldError} role="alert">
              {t('platform.policies.missingKeys', { keys: missing.join(', ') })}
            </span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.policies.effectiveFrom')}</span>
          <input
            className={styles.input}
            type="date"
            value={effectiveFrom}
            required
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
          <span className={styles.fieldHint}>{t('platform.policies.publishEffectiveHint')}</span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.policies.colChangeReason')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={changeReason}
            required
            aria-invalid={changeReason.length > 0 && changeReason.trim().length < MIN_REASON}
            onChange={(event) => setChangeReason(event.target.value)}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.policies.publish')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
